import { Form, useToastContext } from '@finbeat/react-toolkit';
import { getClient, getClientDocumentsInfo, getClientPaymentsInfo } from 'axios/clientApi';
import {
    getOrderChecksInfo,
    getOrderCommentsInfo,
    getOrderInfo,
    orderAssignToOperator,
} from 'axios/orderApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { OrderDetailsGeneralInfo, OrderScoring } from 'models/api/order';
import { useNotifyContext } from 'providers/NotifyProvider';
import { useOrderInfoContext } from 'providers/OrderInfoProvider';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Container, FormGroup, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import 'scss/Main.scss';
import ChecksTab from '../OrderInfo/Tabs/ChecksTab';
import ControlButtons from './Additions/ControlButtons';
import './OrderInfo.scss';
import ClientFilesTab from './Tabs/ClientFilesTab';
import ClientTab from './Tabs/ClientTab';
import CommentsTab from './Tabs/CommentsTab';
import DocumentsTab from './Tabs/DocumentsTab';
import OrderTab from './Tabs/OrderTab';
import PaymentsTab from './Tabs/PaymentsTab';

const OrderInfo = ({
    isModal = false,
    generalInfo,
    modalRef,
    cancelAction,
}: {
    isModal?: boolean;
    generalInfo: OrderDetailsGeneralInfo;
    modalRef?: RefObject<HTMLElement>;
    cancelAction?: (dirty?: boolean) => void;
}) => {
    const className = 'order-info';
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const api = useApi();
    const containerRef = useRef(null);
    const [activeTab, setActiveTab] = useState('');
    const { setRequestedDocs } = useOrderInfoContext();
    const { scoring, setScoring, checksTabFormRef, submitOrderMutation } = useOrderInfoContext();
    const [isNextSubmitted, setIsNextSubmitted] = useState<boolean>(false);
    const { addSubscription, removeSubscription } = useNotifyContext();
    const { addToast } = useToastContext();

    useEffect(() => {
        if (isNextSubmitted) {
            submitOrderMutation.mutate(
                generalInfo.orderGeneral.canPassToScoring ? 'next' : 'checkdocs'
            );
        }
    }, [isNextSubmitted]);

    useEffect(() => {
        if (generalInfo.orderGeneral.statusCode === 'checkdocs') setActiveTab('clientFiles');
        else if (hasRole('orderGetChecksInfo')) setActiveTab('checks');
        else setActiveTab('order');
    }, [generalInfo]);

    const assignToOperator = useMutation('assignToOperator', () =>
        orderAssignToOperator(api, generalInfo.id)
    );

    useEffect(() => assignToOperator.mutate(), []);

    useEffect(() => {
        addSubscription({
            key: 'onOrderStatusChange',
            type: 'OrderStatusChange',
            callback: (message) => {
                const info = JSON.parse(message);

                if (info.OrderId === generalInfo.id && info.Status === 'clcancel') {
                    addToast({
                        type: 'info',
                        description: t('BackOfficeOrderCancelledMsg'),
                        autoDeleteTime: null,
                    });
                    cancelAction?.(false);
                }
            },
        });

        return () => {
            removeSubscription('onOrderStatusChange');
        };
    }, [addSubscription, removeSubscription, addToast, cancelAction]);

    return (
        <Container className={className}>
            <FormGroup className={`${className}__nav-card`}>
                <Nav className={`${className}__nav-tabs`} tabs>
                    {hasRole('clientsGetById') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('client')}
                                active={activeTab === 'client'}
                            >
                                {t('BackOfficeClient')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('orderGetInfo') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('order')}
                                active={activeTab === 'order'}
                            >
                                {t('BackOfficeOrder')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('clientsGetPaymentInfo') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('payments')}
                                active={activeTab === 'payments'}
                            >
                                {t('BackOfficePayments')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('clientsGetClientFiles') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('clientFiles')}
                                active={activeTab === 'clientFiles'}
                            >
                                {t('BackOfficeClientFiles')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('orderGetDocuments') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('documents')}
                                active={activeTab === 'documents'}
                            >
                                {t('BackOfficeDocuments')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasRole('orderGetCommentsInfo') && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('comments')}
                                active={activeTab === 'comments'}
                            >
                                {t('BackOfficeComments')}
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
            </FormGroup>
            <div className={`${className}__content-card custom-scrollbar mb-3`} ref={containerRef}>
                <TabContent activeTab={activeTab}>
                    {hasRole('orderGetChecksInfo') && (
                        <TabPane tabId="checks">
                            <Form<OrderScoring>
                                onSubmit={(scr) => {
                                    if (scr.totalCheck?.checkResult === 'Approve') {
                                        scr.totalCheck.rejectReason = '';
                                        scr.totalCheck.rejectReasonLvl2 = '';
                                    } else if (scr.totalCheck?.checkResult === 'Decline') {
                                        scr.totalCheck.approveReason = '';
                                        scr.totalCheck.approveReasonLvl2 = '';
                                    }
                                    setScoring(scr);
                                    setIsNextSubmitted(true);
                                }}
                                defaultValues={scoring || {}}
                                innerRef={checksTabFormRef}
                                clearAfterSubmit={false}
                            >
                                <ChecksTab
                                    checksInfoQueryFn={() =>
                                        getOrderChecksInfo(
                                            api,
                                            generalInfo.id,
                                            generalInfo.orderGeneral.clientId
                                        )
                                    }
                                    isEditable={generalInfo.orderGeneral.canEditOrder}
                                    isOpen={activeTab === 'checks'}
                                    orderId={generalInfo.id}
                                    clientId={generalInfo.orderGeneral.clientId}
                                />
                            </Form>
                        </TabPane>
                    )}

                    {hasRole('clientsGetById') && (
                        <TabPane tabId="client">
                            <ClientTab
                                clientInfoQueryFn={() =>
                                    getClient(
                                        api,
                                        generalInfo.orderGeneral.clientId,
                                        generalInfo.id
                                    )
                                }
                                isEditable={
                                    generalInfo.orderGeneral.canEditOrder &&
                                    hasRole('clientsUpdateDataOnManualCheck')
                                }
                                manualCheck={true}
                                modalRef={modalRef}
                                orderId={generalInfo.id}
                                clientId={generalInfo.orderGeneral.clientId}
                                isOpen={activeTab === 'client'}
                            />
                        </TabPane>
                    )}
                    {hasRole('orderGetInfo') && (
                        <TabPane tabId="order">
                            <OrderTab
                                orderInfoQueryFn={() => getOrderInfo(api, generalInfo.id)}
                                isOpen={activeTab === 'order'}
                                container={containerRef}
                            />
                        </TabPane>
                    )}
                    {hasRole('clientsGetPaymentInfo') && (
                        <TabPane tabId="payments">
                            <PaymentsTab
                                paymentsQueryFn={() =>
                                    getClientPaymentsInfo(
                                        api,
                                        generalInfo.orderGeneral.clientId,
                                        generalInfo.id
                                    )
                                }
                                isOpen={activeTab === 'payments'}
                                container={containerRef}
                                orderId={generalInfo.id}
                            />
                        </TabPane>
                    )}
                    {hasRole('clientsGetClientFiles') && (
                        <TabPane tabId="clientFiles">
                            <ClientFilesTab
                                isEditable={generalInfo.orderGeneral.canEditOrder}
                                documentsQueryFn={() =>
                                    getClientDocumentsInfo(api, generalInfo.orderGeneral.clientId)
                                }
                                isOpen={activeTab === 'clientFiles'}
                                canRequestingDocs={generalInfo.orderGeneral.canUpdDocs}
                                modalRef={modalRef}
                                clientId={generalInfo.orderGeneral.clientId}
                                orderId={generalInfo.id}
                                setRequestedDocs={setRequestedDocs}
                            />
                        </TabPane>
                    )}
                    {hasRole('orderGetDocuments') && (
                        <TabPane tabId="documents">
                            <DocumentsTab id={generalInfo.id} isOpen={activeTab === 'documents'} />
                        </TabPane>
                    )}
                    {hasRole('orderGetCommentsInfo') && (
                        <TabPane tabId="comments">
                            <CommentsTab
                                orderId={generalInfo.id}
                                commentsQueryFn={() => getOrderCommentsInfo(api, generalInfo.id)}
                                isOpen={activeTab === 'comments'}
                            />
                        </TabPane>
                    )}
                </TabContent>
            </div>
            {!isModal && (
                <FormGroup className={`${className}__footer`}>
                    <ControlButtons
                        info={generalInfo}
                        cancelAction={(dirty) => cancelAction && cancelAction(dirty)}
                        onNextSubmit={() => {
                            checksTabFormRef.current?.requestSubmit();
                        }}
                    />
                </FormGroup>
            )}
        </Container>
    );
};

export default OrderInfo;
